import React, { useState, useEffect, useMemo } from 'react';
import { DeviceCreate, ClientUpdate } from '../../api/models';
import { ManagementOauthApi } from '../../api/apis';
import { apiClient } from '../../services/apiClient';
import { clientService } from '../../services/clientService';
import { ClientWithDevices, PaginationParams } from '../../types/client';
import { DeviceDetailModal } from '../devices/DeviceDetailModal';
import AuditHistoryTab from '../audit/AuditHistoryTab';
import { ClientUsersTab } from './ClientUsersTab';
import { useI18n } from '../../contexts/I18nContext';

interface ClientDetailModalProps {
  client: ClientWithDevices | null;
  isOpen: boolean;
  onClose: () => void;
  onDeviceSelect?: (device: DeviceCreate) => void;
  onClientUpdated?: () => void;
}

export const ClientDetailModal: React.FC<ClientDetailModalProps> = ({
  client,
  isOpen,
  onClose,
  onDeviceSelect,
  onClientUpdated
}) => {
  const { formatDate, formatDateTime, t } = useI18n();
  const [activeTab, setActiveTab] = useState<'overview' | 'devices' | 'users' | 'history'>('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedSiren, setEditedSiren] = useState('');
  const [editedActive, setEditedActive] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [devices, setDevices] = useState<DeviceCreate[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationParams>({ page: 1, pageSize: 10 });
  const [totalDevices, setTotalDevices] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedDevice, setSelectedDevice] = useState<DeviceCreate | null>(null);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);

  const managementApi = useMemo(() => new ManagementOauthApi(apiClient.getConfiguration()), []);

  useEffect(() => {
    if (client) {
      setEditedName(client.name);
      setEditedSiren(client.siren || '');
      setEditedActive(client.active ?? true);
    }
  }, [client]);

  useEffect(() => {
    if (isOpen && client && activeTab === 'devices') {
      fetchDevices();
    }
    // fetchDevices is stable within the render; tracking its identity would
    // cause the effect to re-run on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, client, activeTab, pagination.page]);

  const fetchDevices = async () => {
    if (!client || !client.id) return;

    try {
      setDevicesLoading(true);

      // Use dedicated endpoint to fetch devices for this client
      const response = await managementApi.apiGetConfByClientV2V2ConfClientClientIdGet({
        clientId: client.id,
        page: pagination.page,
        pageSize: pagination.pageSize
      });

      // Handle paginated response
      if (response && typeof response === 'object' && 'items' in response) {
        // Paginated response format. This endpoint returns the count as
        // `total` (flat shape), unlike /v2/conf which nests it as
        // pagination.total_items — accept both so the counter updates.
        setDevices(response.items || []);
        setTotalDevices(response.total ?? response.total_items ?? 0);
        setTotalPages(response.total_pages || 1);
      } else if (Array.isArray(response)) {
        // Simple array response (fallback)
        const startIndex = (pagination.page - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        setDevices(response.slice(startIndex, endIndex));
        setTotalDevices(response.length);
        setTotalPages(Math.ceil(response.length / pagination.pageSize));
      } else {
        setDevices([]);
        setTotalDevices(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Failed to fetch devices:', error);
      setDevices([]);
      setTotalDevices(0);
      setTotalPages(0);
    } finally {
      setDevicesLoading(false);
    }
  };

  const sirenValid = editedSiren === '' || /^\d{9}$/.test(editedSiren);

  const handleSave = async () => {
    if (!client || !editedName.trim() || !sirenValid) return;

    try {
      setIsSaving(true);
      const updateData: ClientUpdate = {
        name: editedName.trim(),
        siren: editedSiren.trim() || null,
        active: editedActive
      };

      await managementApi.apiPatchClientV2V2ClientClientIdPatch({
        clientId: client.id,
        clientUpdate: updateData
      });

      // Clear client cache so updated client appears correctly in all dropdowns
      clientService.clearCache();
      setIsEditing(false);
      if (onClientUpdated) {
        onClientUpdated();
      }
    } catch (error) {
      console.error('[CLIENT DETAIL] Failed to update client:', error);
      alert('Failed to update client. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (client) {
      setEditedName(client.name);
      setEditedSiren(client.siren || '');
      setEditedActive(client.active ?? true);
    }
    setIsEditing(false);
  };

  const handleDeviceClick = (device: DeviceCreate) => {
    setSelectedDevice(device);
    setIsDeviceModalOpen(true);
    if (onDeviceSelect) {
      onDeviceSelect(device);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Validate modal should be shown
  if (!isOpen || !client || !client.id) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="client-detail-modal" data-testid="modal-client-detail" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <h2>{t('clients.clientDetails')}</h2>
            <span className="client-id-badge">{client.id}</span>
          </div>
          <button className="close-button" data-testid="btn-modal-close" onClick={onClose} aria-label={t('common.close')}>
            ×
          </button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            data-testid="tab-client-overview"
            onClick={() => setActiveTab('overview')}
          >
            {t('tabs.overview')}
          </button>
          <button
            className={`tab-button ${activeTab === 'devices' ? 'active' : ''}`}
            data-testid="tab-client-devices"
            onClick={() => setActiveTab('devices')}
          >
            {t('devices.devices')} ({client.deviceCount})
          </button>
          <button
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            data-testid="tab-client-users"
            onClick={() => setActiveTab('users')}
          >
            {t('clients.users.tabLabel')}
          </button>
          <button
            className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
            data-testid="tab-client-history"
            onClick={() => setActiveTab('history')}
          >
            {t('audit.history.tabLabel')}
          </button>
        </div>

        <div className="modal-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="client-overview">
                <div className="info-grid">
                  <div className="info-item">
                    <label>{t('clients.clientId')}</label>
                    <span className="info-value monospace">{client.id}</span>
                  </div>
                  <div className="info-item">
                    <label>{t('clients.clientName')}</label>
                    {isEditing ? (
                      <input
                        type="text"
                        data-testid="field-client-name-edit"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        className="edit-input"
                        autoFocus
                      />
                    ) : (
                      <span className="info-value">{client.name}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>{t('clients.sirenLabel')}</label>
                    {isEditing ? (
                      <div className="input-with-validation">
                        <input
                          type="text"
                          data-testid="field-client-siren-edit"
                          inputMode="numeric"
                          value={editedSiren}
                          onChange={(e) => setEditedSiren(e.target.value.replace(/\D/g, '').slice(0, 9))}
                          className={`edit-input${!sirenValid ? ' input-error' : ''}`}
                          placeholder="123456789"
                          maxLength={9}
                        />
                        {editedSiren && !sirenValid && (
                          <span className="field-validation-error" data-testid="error-client-siren">{t('clients.sirenDigitsHint')}</span>
                        )}
                        {editedSiren && sirenValid && (
                          <span className="field-validation-ok">{t('common.confirm')}</span>
                        )}
                      </div>
                    ) : (
                      <span className="info-value monospace">{client.siren || '-'}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>{t('clients.status')}</label>
                    {isEditing ? (
                      <select
                        data-testid="field-client-active-edit"
                        value={editedActive ? 'active' : 'inactive'}
                        onChange={(e) => setEditedActive(e.target.value === 'active')}
                        className="edit-select status-select"
                      >
                        <option value="active" className="status-option-active">{t('clients.active')}</option>
                        <option value="inactive" className="status-option-inactive">{t('clients.inactive')}</option>
                      </select>
                    ) : (
                      <span className={`status-badge ${client.active ? 'active' : 'inactive'}`}>
                        {client.active ? t('clients.active') : t('clients.inactive')}
                      </span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>{t('clients.totalDevices')}</label>
                    <span className="info-value">
                      <span className="device-count-large">{client.deviceCount}</span>
                    </span>
                  </div>
                  <div className="info-item">
                    <label>{t('clients.lastActivity')}</label>
                    <span className="info-value">
                      {client.lastActivity ? formatDateTime(client.lastActivity) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>{t('clients.created')}</label>
                    <span className="info-value">
                      {formatDateTime(client.created_at)}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>{t('clients.updated')}</label>
                    <span className="info-value">
                      {formatDateTime(client.updated_at)}
                    </span>
                  </div>
                </div>

                {isEditing && (
                  <div className="edit-actions">
                    <button
                      onClick={handleSave}
                      data-testid="btn-client-save"
                      className="btn btn-primary"
                      disabled={isSaving || !editedName.trim() || !sirenValid}
                    >
                      {isSaving ? `${t('common.save')}…` : t('common.save')}
                    </button>
                    <button
                      onClick={handleCancel}
                      data-testid="btn-client-cancel"
                      className="btn btn-secondary"
                      disabled={isSaving}
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'devices' && (
            <div className="devices-tab">
              <div className="devices-list">
                <div className="devices-header">
                  <h3>{t('clients.associatedDevices')}</h3>
                  <span className="devices-count">
                    {totalDevices} {t('audit.stats.total').toLowerCase()}
                  </span>
                </div>

                {devicesLoading ? (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading devices...</p>
                  </div>
                ) : devices.length > 0 ? (
                  <>
                    <div className="device-list-container">
                      {devices.map((device) => (
                        <div
                          key={device.idDevice}
                          className="device-list-item"
                          onClick={() => handleDeviceClick(device)}
                        >
                          <div className="device-info">
                            <div className="device-header">
                              <span className="device-id">{device.idDevice}</span>
                              <span className={`device-status ${device.needsRefresh ? 'needs-refresh' : 'active'}`}>
                                {device.needsRefresh ? 'Needs Refresh' : 'Active'}
                              </span>
                            </div>
                            <div className="device-details">
                              <span className="device-type">{device.deviceType || 'Unknown'}</span>
                              {device.phone && device.phone !== '000000000000000' && (
                                <span className="device-phone">{device.phone}</span>
                              )}
                              <span className="device-updated">
                                Updated: {formatDate(device.dateTime)}
                              </span>
                            </div>
                          </div>
                          <div className="device-arrow">→</div>
                        </div>
                      ))}
                    </div>

                    {totalPages > 1 && (
                      <div className="pagination">
                        <button
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page === 1}
                          className="pagination-btn"
                        >
                          Previous
                        </button>

                        <span className="page-info">
                          Page {pagination.page} of {totalPages}
                        </span>

                        <button
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page === totalPages}
                          className="pagination-btn"
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="no-devices">
                    <p>No devices associated with this client.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <ClientUsersTab
              clientId={client.id}
              clientName={client.name}
              clientAccess={client.provisioning_access ?? true}
            />
          )}

          {activeTab === 'history' && (
            <AuditHistoryTab
              resourceType="client"
              resourceId={client.id}
            />
          )}
        </div>

        <div className="modal-actions">
          <div className="action-group">
            {activeTab === 'overview' && !isEditing && (
              <button onClick={() => setIsEditing(true)} data-testid="btn-client-edit" className="btn btn-primary">
                Edit Client
              </button>
            )}
          </div>
          <button onClick={onClose} data-testid="btn-client-close" className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>

      {selectedDevice && (
        <DeviceDetailModal
          device={selectedDevice}
          isOpen={isDeviceModalOpen}
          onClose={() => {
            setIsDeviceModalOpen(false);
            setSelectedDevice(null);
          }}
          onDeviceUpdated={() => {
            fetchDevices();
          }}
        />
      )}
    </div>
  );
};
